import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/header';
import Footer from '../../../components/footer';
import SEO from '../../../components/SEO';
import '../../artikler/styles/artikler-clean.css';
import './google-indeksering.css';

const GoogleIndeksering = () => {
  const [readingProgress, setReadingProgress] = useState(0);
  const [activeSection, setActiveSection] = useState('');
  const [isManualScroll, setIsManualScroll] = useState(false);

  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const totalScroll = document.documentElement.scrollTop;
          const windowHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
          setReadingProgress(totalScroll / windowHeight);
          ticking = false;
        });
        ticking = true;
      }
    };

    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -70% 0px',
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      if (!isManualScroll) {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      }
    }, observerOptions);

    tableOfContents.forEach((item) => {
      const element = document.getElementById(item.id);
      if (element) {
        observer.observe(element);
      }
    });

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, [isManualScroll]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      setActiveSection(sectionId);
      setIsManualScroll(true);
      
      const header = document.querySelector('header');
      const headerHeight = header ? header.getBoundingClientRect().height : 80;
      const extraSpace = 100;
      const totalOffset = headerHeight + extraSpace;
      
      const elementRect = element.getBoundingClientRect();
      const elementTop = elementRect.top + window.pageYOffset;
      const scrollToPosition = elementTop - totalOffset;
      
      window.scrollTo({
        top: Math.max(0, scrollToPosition),
        behavior: 'smooth'
      });

      setTimeout(() => {
        setIsManualScroll(false);
      }, 1000);
    }
  };

  const tableOfContents = [
    { id: 'intro', title: 'Introduktion' },
    { id: 'hvad-er-indeksering', title: 'Hvad er indeksering?' },
    { id: 'crawling-processen', title: 'Crawling-processen' },
    { id: 'rendering', title: 'Rendering af sider' },
    { id: 'indeksering', title: 'Indekseringsprocessen' },
    { id: 'ranking-faktorer', title: 'Ranking faktorer' },
    { id: 'saadan-checker-du', title: 'Sådan tjekker du status' },
    { id: 'optimering', title: 'Optimering af indeksering' },
    { id: 'almindelige-problemer', title: 'Almindelige problemer' },
    { id: 'konklusion', title: 'Konklusion' }
  ];

  return (
    <div className="artikel-side google-indeksering-artikel">
      <SEO 
        title="Hvordan Google Indekserer Din Hjemmeside - Komplet Guide | Parla Branding"
        description="Lær præcis hvordan Google finder, crawler, render og indekserer din hjemmeside. Forstå crawling-processen, ranking faktorer og optimer din side til bedre synlighed."
        keywords="google indeksering, crawler, googlebot, seo crawling, website indeksering, søgemaskine crawler, google search console, sitemap, robots.txt"
        url="https://parlabranding.dk/artikler/google-indeksering"
        canonicalUrl="https://www.parlabranding.dk/artikler/google-indeksering"
        type="article"
      />
      <Header />

      {/* Reading Progress Bar */}
      <div className="reading-progress">
        <div 
          className="reading-progress-bar" 
          style={{ width: `${readingProgress * 100}%` }}
        ></div>
      </div>

      {/* Hero Section */}
      <section className="artikel-hero">
        <div className="container">
          <div className="artikel-hero-content">
            <div className="hero-content-left">
              <div className="artikel-meta">
                <span className="artikel-kategori">SEO Guide</span>
                <span className="artikel-dato">4. november 2025</span>
                <span className="artikel-læsetid">10 min læsning</span>
              </div>
              
              <h1 className="artikel-title">
                Hvordan Google Indekserer Din Hjemmeside - Den Komplette Guide
              </h1>
              
              <p className="artikel-subtitle">
                Forstå den magiske proces bag hvordan Google finder, læser og rangerer din hjemmeside. 
                Fra crawling til indeksering - få indsigt i søgemaskinens indre mekanik og lær hvordan 
                du sikrer at din side bliver fundet og vist i søgeresultaterne.
              </p>
              
              <div className="artikel-author">
                <div className="author-avatar">PB</div>
                <div className="author-info">
                  <strong>Skrevet af Parla Branding</strong>
                  <span>SEO eksperter siden 2021</span>
                </div>
              </div>
            </div>

            <div className="hero-content-right">
              <div className="hero-image-container">
                <img 
                  src="https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80"
                  alt="Google indeksering process - Server og søgemaskine visualisering"
                  className="hero-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="artikel-content">
        <div className="container">
          {/* Breadcrumbs */}
          <nav className="breadcrumbs" aria-label="Breadcrumb">
            <ol className="breadcrumb-list">
              <li className="breadcrumb-item">
                <a href="/">Hjem</a>
              </li>
              <li className="breadcrumb-separator">›</li>
              <li className="breadcrumb-item">
                <a href="/artikler">Artikler</a>
              </li>
              <li className="breadcrumb-separator">›</li>
              <li className="breadcrumb-item current" aria-current="page">
                Google Indeksering
              </li>
            </ol>
          </nav>

          <div className="artikel-layout">
            
            {/* Left Sidebar - Table of Contents */}
            <aside className="artikel-toc-sidebar">
              <h3>Indholdsfortegnelse</h3>
              <ul className="toc-list">
                {tableOfContents.map((item) => (
                  <li key={item.id}>
                    <button
                      className={activeSection === item.id ? 'active' : ''}
                      onClick={() => scrollToSection(item.id)}
                      type="button"
                    >
                      {item.title}
                    </button>
                  </li>
                ))}
              </ul>
            </aside>

            <div className="artikel-main">

              {/* Introduction */}
              <section className="artikel-section" id="intro">
                <div className="artikel-text">
                  <p className="lead">
                    Har du nogensinde tænkt over hvordan Google egentlig finder din hjemmeside? Hvordan 
                    ved søgemaskinen hvad din side handler om, og hvorfor vises nogle sider øverst mens 
                    andre gemmer sig på side 10? Svaret ligger i en fascinerende proces kaldet indeksering.
                  </p>
                  
                  <p>
                    Forestil dig Google som et kæmpe bibliotek - måske verdens største. Men i modsætning 
                    til et fysisk bibliotek, hvor bibliotekaren selv placerer bøgerne på hylderne, skal 
                    Google automatisk finde, læse og kategorisere milliarder af hjemmesider. Hver eneste dag.
                  </p>

                  <p>
                    I denne guide tager jeg dig med bag kulisserne på Google's indekseringsproces. Du lærer 
                    præcis hvordan Googlebot (Google's crawler) finder din side, hvordan den læser indholdet, 
                    og hvad du kan gøre for at sikre at din hjemmeside bliver indekseret korrekt. Dette er 
                    fundamentalt for enhver der ønsker synlighed i søgeresultaterne.
                  </p>

                  <div className="highlight-box">
                    <h4>Hvad du lærer i denne guide:</h4>
                    <ul>
                      <li>Forskellen mellem crawling, rendering og indeksering</li>
                      <li>Hvordan Googlebot finder og besøger din hjemmeside</li>
                      <li>Hvad der sker når Google indekserer en side</li>
                      <li>Hvilke faktorer der påvirker din ranking</li>
                      <li>Sådan tjekker du om din side er indekseret</li>
                      <li>Praktiske tips til at optimere indekseringen</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Hvad er indeksering */}
              <section className="artikel-section" id="hvad-er-indeksering">
                <h2>Hvad er Indeksering? Grundlæggende Forståelse</h2>
                
                <div className="artikel-text">
                  <p>
                    Før vi dykker ned i den tekniske proces, lad os starte med det basale: Hvad betyder 
                    det egentlig at blive indekseret af Google?
                  </p>

                  <div className="definition-card">
                    <h3>Definition</h3>
                    <p>
                      <strong>Indeksering</strong> er processen hvor Google analyserer og gemmer information 
                      om hjemmesider i sin massive database (indekset). Kun sider der er i Google's indeks 
                      kan vises i søgeresultaterne. Tænk på det som forskellen mellem at have en bog 
                      liggende i hjemmet versus at have den katalogiseret i bibliotekets system.
                    </p>
                  </div>

                  <p>
                    Når vi siger at en side er "indekseret", betyder det at Google har:
                  </p>
                  
                  <ul className="simple-list">
                    <li>Fundet siden (discovery)</li>
                    <li>Besøgt siden og hentet dens indhold (crawling)</li>
                    <li>Analyseret og forstået sidens indhold (processing)</li>
                    <li>Gemt informationen i sin database (indexing)</li>
                    <li>Gjort siden søgbar i Google Search (visibility)</li>
                  </ul>

                  <p>
                    Men her er den vigtige pointe: <strong>Ikke alle sider der bliver crawlet, bliver 
                    nødvendigvis indekseret</strong>. Google er selektiv. Sider med lavt kvalitetsindhold, 
                    duplikeret content eller tekniske problemer kan blive crawlet men afvist fra indekset.
                  </p>

                  <div className="comparison-box">
                    <h4>Crawling vs. Indeksering vs. Ranking - Forskellen</h4>
                    <p>Disse tre begreber bliver ofte blandet sammen, men de er forskellige faser:</p>
                    <div className="comparison-grid">
                      <div className="comparison-item">
                        <h5>1. Crawling</h5>
                        <p>
                          Google's robot (Googlebot) besøger din side og downloader HTML, CSS, JavaScript 
                          og andre ressourcer. Dette er opdagelse og datahentning.
                        </p>
                      </div>
                      <div className="comparison-item">
                        <h5>2. Indeksering</h5>
                        <p>
                          Google analyserer det crawlede indhold, forstår hvad siden handler om, og 
                          beslutter om den skal gemmes i indekset. Dette er analyse og lagring.
                        </p>
                      </div>
                      <div className="comparison-item">
                        <h5>3. Ranking</h5>
                        <p>
                          Når nogen søger, vælger Google hvilke indekserede sider der skal vises og 
                          i hvilken rækkefølge. Dette er præsentation og prioritering.
                        </p>
                      </div>
                    </div>
                  </div>

                  <p>
                    Forestil dig det som en ansøgningsproces: Crawling er når Google modtager din ansøgning, 
                    indeksering er når de beslutter at gemme den i deres system, og ranking er når de 
                    vurderer hvor godt du matcher det specifikke job (søgeforespørgsel).
                  </p>
                </div>
              </section>

              {/* Crawling Processen */}
              <section className="artikel-section" id="crawling-processen">
                <h2>Crawling-Processen - Sådan Finder Google Din Side</h2>
                
                <div className="artikel-text">
                  <p>
                    Alt starter med crawling. Googlebot - Google's sofistikerede web crawler - er 
                    konstant på jagt efter nye og opdaterede sider på internettet. Men hvordan finder 
                    den præcis din hjemmeside blandt milliarder af andre?
                  </p>

                  <h3>Sådan Opdager Googlebot Nye Sider</h3>
                  <p>
                    Google bruger primært tre metoder til at finde nye sider:
                  </p>

                  <h4>1. Links fra Eksisterende Sider</h4>
                  <p>
                    Den mest almindelige måde. Når Googlebot crawler en side, følger den alle links på 
                    den side. Hvis en etableret hjemmeside linker til din nye side, vil Googlebot opdage 
                    den næste gang den crawler den etablerede side. Dette er derfor backlinks er så 
                    vigtige - de fungerer som vejskilte der guider Google til dit indhold.
                  </p>

                  <h4>2. Sitemap Indsendelse</h4>
                  <p>
                    En XML sitemap er som et katalog over alle vigtige sider på din hjemmeside. Ved at 
                    uploade en sitemap til <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer" className="external-link">Google Search Console</a>, 
                    fortæller du direkte til Google hvilke sider der eksisterer. Dette er især nyttigt for:
                  </p>
                  <ul className="simple-list">
                    <li>Nye hjemmesider uden mange eksterne links endnu</li>
                    <li>Store sider med mange undersider der kan være svære at finde</li>
                    <li>Sider med meget dynamisk indhold der opdateres ofte</li>
                  </ul>

                  <h4>3. Manuel URL Indsendelse</h4>
                  <p>
                    Gennem Google Search Console kan du manuelt anmode om at Google crawler en specifik 
                    URL. Dette er nyttigt når du har publiceret vigtigt nyt indhold og ønsker hurtig 
                    indeksering i stedet for at vente på at Googlebot finder det naturligt.
                  </p>

                  <h3>Crawl Budget - Hvor Meget Crawler Google?</h3>
                  <p>
                    Her er noget mange ikke ved: Google crawler ikke hele din hjemmeside hver dag. 
                    Hvert website får tildelt et såkaldt <strong>crawl budget</strong> - et maksimalt 
                    antal sider Googlebot vil crawle inden for en given periode.
                  </p>

                  <p>
                    Crawl budget afhænger af flere faktorer:
                  </p>

                  <ul className="simple-list">
                    <li><strong>Sidens autoritet:</strong> Store, autoritative sider som dr.dk får et større crawl budget end en lille lokal virksomhed</li>
                    <li><strong>Opdateringsfrekvens:</strong> Sider der opdateres ofte bliver crawlet oftere</li>
                    <li><strong>Server performance:</strong> Hvis din server er langsom eller ustabil, reducerer Google crawl budgettet for ikke at overbelaste den</li>
                    <li><strong>Crawl errors:</strong> Mange 404-fejl eller server errors kan reducere hvor meget Google crawler</li>
                  </ul>

                  <div className="tip-box">
                    <h4>💡 Praktisk Tip</h4>
                    <p>
                      For små til mellemstore hjemmesider (under 10.000 sider) er crawl budget sjældent 
                      et problem. Google crawler typisk alle vigtige sider indenfor få dage. Det bliver 
                      først relevant for meget store e-commerce sites eller nyhedssider med tusindvis 
                      af sider.
                    </p>
                  </div>

                  <h3>Robots.txt - Styring af Googlebot</h3>
                  <p>
                    Filen <code>robots.txt</code> er som en instruktionsbog til web crawlers. Den ligger 
                    i roden af din hjemmeside (f.eks. parlabranding.dk/robots.txt) og fortæller Googlebot 
                    hvilke områder den må og ikke må crawle.
                  </p>

                  <div className="code-example">
                    <h5>Eksempel på en simpel robots.txt fil:</h5>
                    <pre><code>{`# Tillad alle crawlers adgang til hele sitet
User-agent: *
Allow: /

# Bloker admin-området
Disallow: /admin/

# Bloker midlertidige filer
Disallow: /temp/

# Angiv placering af sitemap
Sitemap: https://parlabranding.dk/sitemap.xml`}</code></pre>
                  </div>

                  <div className="warning-box">
                    <h4>⚠️ Almindelig Fejl</h4>
                    <p>
                      Mange blokerer ved et uheld vigtige sider i robots.txt. Dobbelt-tjek altid at du 
                      ikke blokerer sider du rent faktisk ØNSKER indekseret. En enkel "Disallow: /" 
                      blokerer hele dit website fra at blive crawlet!
                    </p>
                  </div>
                </div>
              </section>

              {/* Rendering */}
              <section className="artikel-section" id="rendering">
                <h2>Rendering - Når Google Ser Din Side Som en Bruger</h2>
                
                <div className="artikel-text">
                  <p>
                    Moderne hjemmesider er ikke bare simpel HTML. De bruger JavaScript til at skabe 
                    dynamisk indhold, animationer og interaktive elementer. Men kan Google egentlig 
                    se alt dette JavaScript-genererede indhold?
                  </p>

                  <p>
                    Svaret er: Ja, men med visse begrænsninger. Google har udviklet sofistikeret 
                    teknologi til at "rendere" sider - altså eksekvere JavaScript og se siden som 
                    en rigtig bruger ville se den i browseren.
                  </p>

                  <h3>To-Faset Indeksering</h3>
                  <p>
                    Google bruger en to-faset proces når den håndterer JavaScript-tunge sider:
                  </p>

                  <h4>Fase 1: Initial Crawling</h4>
                  <p>
                    Googlebot downloader HTML'en og parser den øjeblikkeligt. Alt indhold der er 
                    tilgængeligt i den rå HTML (server-side rendered content) bliver set og kan 
                    påvirke rankings næsten med det samme.
                  </p>

                  <h4>Fase 2: Rendering og Indeksering</h4>
                  <p>
                    JavaScript-ressourcer bliver downloadet og eksekveret. Dette sker ofte nogle timer 
                    eller endda dage senere, afhængigt af Googles ressourcer. Indhold der kun vises 
                    via JavaScript bliver først indekseret i denne fase.
                  </p>

                  <div className="info-box">
                    <h4>Hvorfor er dette vigtigt?</h4>
                    <p>
                      Hvis dit vigtigste indhold (f.eks. produktbeskrivelser eller artikeltekst) kun 
                      bliver indlæst via JavaScript, kan der være en forsinkelse før Google ser og 
                      indekserer det. For optimal SEO bør kritisk indhold være tilgængeligt i den 
                      initiale HTML.
                    </p>
                  </div>

                  <h3>Server-Side Rendering vs. Client-Side Rendering</h3>
                  <p>
                    Der er to primære måder at bygge moderne websites på, og de påvirker hvordan 
                    Google ser din side:
                  </p>

                  <h4>Server-Side Rendering (SSR)</h4>
                  <p>
                    Serveren genererer den fulde HTML før den sendes til browseren. Googlebot ser 
                    alt indhold øjeblikkeligt uden at skulle eksekvere JavaScript. Dette er ideelt 
                    for SEO. Frameworks som Next.js og Nuxt.js specialiserer sig i SSR.
                  </p>

                  <h4>Client-Side Rendering (CSR)</h4>
                  <p>
                    Browseren får minimal HTML og JavaScript bygger indholdet dynamisk. Googlebot 
                    skal vente på rendering-fasen for at se indholdet. React, Vue og Angular apps 
                    bruger ofte CSR, hvilket kan forsinke indeksering.
                  </p>

                  <div className="tip-box">
                    <h4>💡 Best Practice</h4>
                    <p>
                      Hvis du bygger en ny hjemmeside og SEO er vigtigt, overvej frameworks der 
                      supporter SSR eller static site generation (SSG). Hos <Link to="/tjenester" className="internal-link">Parla Branding bygger vi altid med SEO i tankerne</Link>, 
                      hvilket betyder vi vælger teknologier der sikrer optimal crawlability.
                    </p>
                  </div>

                  <h3>Sådan Tester Du Googles Rendering</h3>
                  <p>
                    Du kan se præcis hvordan Google render din side gennem Google Search Console's 
                    "URL Inspection" værktøj. Det viser dig både den rå HTML og den rendered version, 
                    så du kan sammenligne og identificere eventuelle problemer.
                  </p>
                </div>
              </section>

              {/* Indekseringsprocessen */}
              <section className="artikel-section" id="indeksering">
                <h2>Indekseringsprocessen - Fra Rå Data til Søgbart Indhold</h2>
                
                <div className="artikel-text">
                  <p>
                    Efter Google har crawlet og renderet din side, begynder den egentlige indeksering. 
                    Dette er hvor Google analyserer indholdet i dybden og beslutter hvordan (og om) 
                    det skal gemmes i indekset.
                  </p>

                  <h3>Hvad Sker Der Under Indeksering?</h3>
                  <p>
                    Googles indekseringssystem udfører en række sofistikerede analyser:
                  </p>

                  <h4>1. Content Analyse og Forståelse</h4>
                  <p>
                    Google bruger avanceret natural language processing (NLP) til at forstå hvad din 
                    side handler om. Den identificerer:
                  </p>
                  <ul className="simple-list">
                    <li><strong>Primære emner:</strong> Hvad er sidens hovedfokus?</li>
                    <li><strong>Entiteter:</strong> Hvilke personer, steder, produkter eller koncepter nævnes?</li>
                    <li><strong>Kontekst:</strong> Hvordan relaterer indholdet til andre emner?</li>
                    <li><strong>Kvalitet signals:</strong> Er indholdet originalt, nyttigt og velskrevet?</li>
                  </ul>

                  <h4>2. Strukturel Analyse</h4>
                  <p>
                    Google analyserer sidens HTML-struktur for at forstå informationshierarkiet:
                  </p>
                  <ul className="simple-list">
                    <li><strong>Title tag:</strong> Den vigtigste indikator for sidens emne</li>
                    <li><strong>Overskrifter (H1, H2, H3):</strong> Struktur og underemner</li>
                    <li><strong>Meta beskrivelse:</strong> Hjælper med at forstå sidens formål</li>
                    <li><strong>Struktureret data:</strong> Schema markup der giver eksplicitte hints</li>
                    <li><strong>Internal links:</strong> Hvordan siden relaterer til resten af sitet</li>
                  </ul>

                  <div className="code-example">
                    <h5>Eksempel på god HTML struktur for indeksering:</h5>
                    <pre><code>{`<!DOCTYPE html>
<html lang="da">
<head>
  <title>Hvordan Google Indekserer - Komplet Guide | Parla</title>
  <meta name="description" content="Lær hvordan Google finder, 
    crawler og indekserer din hjemmeside. Praktisk guide til bedre SEO.">
  <link rel="canonical" href="https://parlabranding.dk/artikler/google-indeksering">
</head>
<body>
  <article>
    <h1>Hvordan Google Indekserer Din Hjemmeside</h1>
    
    <section>
      <h2>Crawling-Processen</h2>
      <p>Google bruger crawlers til at finde sider...</p>
      
      <h3>Googlebot Discovery</h3>
      <p>Der er tre primære måder...</p>
    </section>
  </article>
</body>
</html>`}</code></pre>
                  </div>

                  <h4>3. Duplikat Detection</h4>
                  <p>
                    Google er ekstremt god til at identificere duplikeret indhold. Hvis din side er 
                    identisk eller meget lig eksisterende sider (enten på dit eget site eller andre 
                    sites), kan Google vælge at:
                  </p>
                  <ul className="simple-list">
                    <li>Ikke indeksere siden overhovedet</li>
                    <li>Indeksere den men give den meget lav prioritet</li>
                    <li>Vælge en "canonical" version hvis der er flere versioner af samme side</li>
                  </ul>

                  <h4>4. Kvalitetsvurdering</h4>
                  <p>
                    Google evaluerer indholdet baseret på E-E-A-T principper (Experience, Expertise, 
                    Authoritativeness, Trustworthiness). Selv hvis en side bliver indekseret, påvirker 
                    kvalitetsvurderingen hvor højt den kan ranke.
                  </p>

                  <h3>Canonicalization - Håndtering af Duplikater</h3>
                  <p>
                    Mange hjemmesider har uundgåeligt nogle duplikatsider. For eksempel:
                  </p>
                  <ul className="simple-list">
                    <li>HTTP vs HTTPS versioner</li>
                    <li>Med og uden www</li>
                    <li>URL parametre (f.eks. ?ref=facebook)</li>
                    <li>Print-versioner af sider</li>
                  </ul>

                  <p>
                    Canonical tags fortæller Google hvilken version der er "originalen":
                  </p>

                  <div className="code-example">
                    <pre><code>{`<!-- På alle versioner af siden, peg på den foretrukne version -->
<link rel="canonical" href="https://www.parlabranding.dk/artikler/google-indeksering">`}</code></pre>
                  </div>

                  <h3>Hvornår Vælger Google IKKE at Indeksere?</h3>
                  <p>
                    Bare fordi Google crawler din side, betyder det ikke den bliver indekseret. 
                    Google kan afvise indeksering hvis:
                  </p>

                  <ul className="simple-list">
                    <li><strong>Lav kvalitet:</strong> Tyndt indhold, keyword stuffing, spammy tekst</li>
                    <li><strong>Duplikeret indhold:</strong> Kopieret fra andre sider</li>
                    <li><strong>Tekniske direktiver:</strong> Noindex meta tag, robots.txt blokering</li>
                    <li><strong>Crawl problemer:</strong> Side loader ikke korrekt eller har massive fejl</li>
                    <li><strong>Irrelevans:</strong> Google vurderer at siden ikke tilbyder værdi</li>
                  </ul>

                  <div className="warning-box">
                    <h4>⚠️ Noindex Meta Tag</h4>
                    <p>
                      Nogle gange ønsker du faktisk IKKE at en side bliver indekseret (f.eks. admin 
                      sider, thank you pages, etc.). Brug noindex meta tag:
                    </p>
                    <pre><code>{`<meta name="robots" content="noindex, follow">`}</code></pre>
                    <p>
                      Dette fortæller Google at crawle siden og følge links, men ikke inkludere den 
                      i søgeresultaterne.
                    </p>
                  </div>
                </div>
              </section>

              {/* Ranking Faktorer */}
              <section className="artikel-section" id="ranking-faktorer">
                <h2>Fra Indeksering til Ranking - Hvad Påvirker Din Position?</h2>
                
                <div className="artikel-text">
                  <p>
                    At være indekseret er kun første skridt. Den rigtige udfordring er at ranke højt 
                    når folk søger på relevante termer. Google bruger over 200 ranking faktorer, men 
                    lad os fokusere på de mest impactfulde.
                  </p>

                  <h3>Content Kvalitet og Relevans</h3>
                  <p>
                    Dette er fundamentet. Google's algoritmer er utroligt gode til at evaluere om 
                    dit indhold faktisk besvarer brugerens søgning godt. Nøgleelementer:
                  </p>

                  <ul className="simple-list">
                    <li><strong>Dybde og omfattenhed:</strong> Dækker du emnet grundigt?</li>
                    <li><strong>Originalitet:</strong> Er det unikt indhold eller kopieret?</li>
                    <li><strong>Ekspertise:</strong> Demonstrerer du faglig viden?</li>
                    <li><strong>Brugerengagement:</strong> Bliver folk på siden eller bouncer de tilbage?</li>
                  </ul>

                  <h3>Page Experience og Core Web Vitals</h3>
                  <p>
                    Siden 2021 er brugeroplevelse en officiel ranking faktor. Google måler tre 
                    primære metriks:
                  </p>

                  <h4>Largest Contentful Paint (LCP)</h4>
                  <p>
                    Måler hvor hurtigt den største content på siden loader. Mål: Under 2.5 sekunder.
                  </p>

                  <h4>First Input Delay (FID)</h4>
                  <p>
                    Måler hvor responsivt siden reagerer på brugerinteraktion. Mål: Under 100 millisekunder.
                  </p>

                  <h4>Cumulative Layout Shift (CLS)</h4>
                  <p>
                    Måler visuel stabilitet - hvor meget indholdet "springer" rundt mens det loader. 
                    Mål: Under 0.1.
                  </p>

                  <div className="tip-box">
                    <h4>💡 Test Dine Core Web Vitals</h4>
                    <p>
                      Brug <a href="https://pagespeed.web.dev/" target="_blank" rel="noopener noreferrer" className="external-link">Google PageSpeed Insights</a> til 
                      at se hvordan din side performer. Det giver konkrete forslag til forbedringer.
                    </p>
                  </div>

                  <h3>Mobilvenlig Design</h3>
                  <p>
                    Google bruger "mobile-first indexing" - det betyder de primært ser på den mobile 
                    version af din side når de evaluerer indhold og kvalitet. En hjemmeside der ikke 
                    fungerer godt på mobil vil ranke dårligt.
                  </p>

                  <p>
                    Læs mere om <Link to="/artikler/mobilvenlige-hjemmesider" className="internal-link">hvorfor mobilvenlige hjemmesider er afgørende</Link> for 
                    moderne SEO.
                  </p>

                  <h3>Backlinks og Autoritet</h3>
                  <p>
                    Links fra andre hjemmesider er stadig en af de stærkeste ranking faktorer. De 
                    fungerer som "stemmer" der signalerer til Google at dit indhold er værdifuldt. 
                    Men kvalitet trumfer kvantitet:
                  </p>

                  <ul className="simple-list">
                    <li>Et link fra en autoritativ kilde (f.eks. nyhedssite) væger tungt</li>
                    <li>Relevante links (fra sider i samme industri) er mere værdifulde</li>
                    <li>Hundredvis af spam-links kan faktisk skade din ranking</li>
                  </ul>

                  <h3>HTTPS og Sikkerhed</h3>
                  <p>
                    Sider med SSL-certifikat (HTTPS) får en lille ranking boost. Men vigtigere: 
                    Browsere viser advarsler på HTTP-sider, hvilket øger bounce rate. HTTPS er 
                    standard i 2025.
                  </p>
                </div>
              </section>

              {/* Sådan Checker Du Status */}
              <section className="artikel-section" id="saadan-checker-du">
                <h2>Sådan Tjekker Du Om Din Side Er Indekseret</h2>
                
                <div className="artikel-text">
                  <p>
                    Nu hvor du forstår processen, lad os se på hvordan du faktisk checker om dine 
                    sider er indekseret og hvad deres status er.
                  </p>

                  <h3>Metode 1: Site: Søgning i Google</h3>
                  <p>
                    Den hurtigste måde er en simpel Google søgning. Skriv:
                  </p>

                  <div className="code-example">
                    <pre><code>site:parlabranding.dk</code></pre>
                  </div>

                  <p>
                    Dette viser alle sider fra dit domæne der er i Google's indeks. Du kan også 
                    tjekke specifikke URLs:
                  </p>

                  <div className="code-example">
                    <pre><code>site:parlabranding.dk/artikler/google-indeksering</code></pre>
                  </div>

                  <p>
                    Hvis siden vises, er den indekseret. Hvis ikke, er den enten ikke crawlet endnu 
                    eller afvist fra indeksering.
                  </p>

                  <h3>Metode 2: Google Search Console</h3>
                  <p>
                    For detaljeret information er Google Search Console (GSC) uundværlig. Det er et 
                    gratis værktøj fra Google der giver dyb indsigt i hvordan Google ser din hjemmeside.
                  </p>

                  <h4>URL Inspection Tool</h4>
                  <p>
                    I GSC kan du indtaste enhver URL fra dit site og se:
                  </p>
                  <ul className="simple-list">
                    <li>Om den er indekseret</li>
                    <li>Hvornår den sidst blev crawlet</li>
                    <li>Eventuelle indekseringsproblemer</li>
                    <li>Hvordan Googlebot ser den rendered version</li>
                    <li>Om der er mobile usability issues</li>
                  </ul>

                  <h4>Coverage Report</h4>
                  <p>
                    Coverage rapporten viser status for alle dine sider:
                  </p>
                  <ul className="simple-list">
                    <li><strong>Valid:</strong> Sider der er succesfuldt indekseret</li>
                    <li><strong>Valid with warnings:</strong> Indekseret men med mindre problemer</li>
                    <li><strong>Error:</strong> Sider der ikke kunne indekseres pga. fejl</li>
                    <li><strong>Excluded:</strong> Bevidst ekskluderede sider (f.eks. via noindex)</li>
                  </ul>

                  <div className="info-box">
                    <h4>Setup Google Search Console</h4>
                    <p>
                      Hvis du ikke allerede bruger GSC, bør du sætte det op øjeblikkeligt. Det er 
                      gratis og giver uvurderlig data om din SEO performance. Gå til{' '}
                      <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer" className="external-link">search.google.com/search-console</a> for 
                      at komme i gang.
                    </p>
                  </div>

                  <h3>Metode 3: Sitemap Status</h3>
                  <p>
                    Efter du har indsendt din XML sitemap i GSC, kan du se hvor mange af de indsendte 
                    URLs der faktisk er blevet indekseret. Dette giver dig et hurtigt overblik over 
                    indekseringssundhed.
                  </p>

                  <p>
                    Hvis du har indsendt 100 sider men kun 50 er indekseret, er der åbenlyse problemer 
                    der skal undersøges.
                  </p>
                </div>
              </section>

              {/* Optimering */}
              <section className="artikel-section" id="optimering">
                <h2>Optimering af Indeksering - Praktiske Handlingstrin</h2>
                
                <div className="artikel-text">
                  <p>
                    Lad os komme til det konkrete: Hvad kan du gøre for at sikre optimal indeksering 
                    af din hjemmeside? Her er de mest effektive taktikker.
                  </p>

                  <h3>1. Opret og Indsend en XML Sitemap</h3>
                  <p>
                    En XML sitemap er den mest direkte måde at kommunikere med Google om hvilke 
                    sider der eksisterer på dit site. Den skal:
                  </p>
                  <ul className="simple-list">
                    <li>Liste alle vigtige URLs på dit site</li>
                    <li>Inkludere "lastmod" dato så Google ved hvad der er opdateret</li>
                    <li>Opdateres automatisk når nyt indhold publiceres</li>
                    <li>Indsendes gennem Google Search Console</li>
                  </ul>

                  <p>
                    De fleste moderne CMS systemer (WordPress, Shopify, etc.) genererer automatisk 
                    sitemaps. Hvis du bygger custom, kan du bruge biblioteker eller værktøjer til at 
                    generere dem.
                  </p>

                  <h3>2. Optimer Din Internal Linking Struktur</h3>
                  <p>
                    Google opdager sider gennem links. En god internal linking strategi sikrer at:
                  </p>
                  <ul className="simple-list">
                    <li>Alle vigtige sider er maksimalt 3 klik fra hjemmesiden</li>
                    <li>Vigtige sider modtager flest interne links (signalerer vigtighed)</li>
                    <li>Anchor text er beskrivende (hjælper Google forstå linkede siders indhold)</li>
                    <li>Der ikke er "orphan pages" uden nogen links til dem</li>
                  </ul>

                  <h3>3. Brug Struktureret Data (Schema Markup)</h3>
                  <p>
                    Schema markup er kode du tilføjer til din HTML der hjælper Google forstå dit 
                    indhold mere præcist. For eksempel kan du markere:
                  </p>
                  <ul className="simple-list">
                    <li>Artikler med forfatter, publiceringsdato og kategori</li>
                    <li>Produkter med pris, tilgængelighed og ratings</li>
                    <li>Events med dato, lokation og billetter</li>
                    <li>Virksomhedsinfo med adresse, telefon og åbningstider</li>
                  </ul>

                  <p>
                    Dette kan også give dig "rich snippets" i søgeresultaterne - de visuelle 
                    forbedringer som stjerner, billeder og ekstra info der får dit resultat til 
                    at stikke ud.
                  </p>

                  <div className="code-example">
                    <h5>Eksempel på artikel schema markup:</h5>
                    <pre><code>{`<script type="application/ld+json">
{
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Hvordan Google Indekserer Din Side",
  "author": {
    "@type": "Organization",
    "name": "Parla Branding"
  },
  "datePublished": "2025-11-04",
  "image": "https://parlabranding.dk/images/google-indexing.jpg"
}
</script>`}</code></pre>
                  </div>

                  <h3>4. Optimer Page Speed</h3>
                  <p>
                    Hurtigere sider bliver crawlet mere effektivt (Google kan crawle flere sider 
                    inden for samme crawl budget) og ranker generelt bedre. Fokuser på:
                  </p>
                  <ul className="simple-list">
                    <li>Komprimér billeder (brug WebP format)</li>
                    <li>Minificér CSS og JavaScript</li>
                    <li>Brug browser caching</li>
                    <li>Overvej et CDN for global hastighedsforbedring</li>
                    <li>Lazy load billeder der ikke er i initial viewport</li>
                  </ul>

                  <h3>5. Skriv Unikt, Værdifuldt Indhold</h3>
                  <p>
                    Dette kan ikke understreges nok. Google's algoritmer er designet til at finde 
                    og promovere indhold der faktisk hjælper brugere. Spørg dig selv:
                  </p>
                  <ul className="simple-list">
                    <li>Besvarer dette indhold et specifikt spørgsmål eller behov?</li>
                    <li>Er det mere omfattende end eksisterende indhold om emnet?</li>
                    <li>Har jeg ekspertise eller unik vinkel på emnet?</li>
                    <li>Ville en rigtig person finde dette nyttigt?</li>
                  </ul>

                  <h3>6. Sørg for Mobilvenlig Design</h3>
                  <p>
                    Med mobile-first indexing SKAL din side fungere perfekt på mobile enheder. 
                    Test dette med <a href="https://search.google.com/test/mobile-friendly" target="_blank" rel="noopener noreferrer" className="external-link">Google's Mobile-Friendly Test</a>.
                  </p>

                  <div className="tip-box">
                    <h4>💡 Hos Parla Branding</h4>
                    <p>
                      Alle disse optimeringsteknikker er bygget ind i vores udviklingproces. 
                      Når du får en <Link to="/tjenester" className="internal-link">hjemmeside fra os</Link>, er 
                      den allerede optimeret til crawling og indeksering fra dag ét. Vi tager os 
                      af de tekniske detaljer, så du kan fokusere på dit indhold og din forretning.
                    </p>
                  </div>
                </div>
              </section>

              {/* Almindelige Problemer */}
              <section className="artikel-section" id="almindelige-problemer">
                <h2>Almindelige Indekseringsproblemer og Deres Løsninger</h2>
                
                <div className="artikel-text">
                  <p>
                    Selv med de bedste intentioner kan der opstå problemer. Her er de mest almindelige 
                    indekseringsproblemer jeg ser, og hvordan du løser dem.
                  </p>

                  <h3>Problem 1: "Discovered - Currently Not Indexed"</h3>
                  <p>
                    <strong>Hvad det betyder:</strong> Google har fundet din side (gennem sitemap 
                    eller links) men har valgt ikke at indeksere den endnu.
                  </p>
                  <p>
                    <strong>Mulige årsager:</strong>
                  </p>
                  <ul className="simple-list">
                    <li>Lav kvalitet eller tyndt indhold</li>
                    <li>Duplikeret indhold</li>
                    <li>Utilstrækkelig crawl budget (på meget store sites)</li>
                    <li>Siden er relativt ny og Google har bare ikke nået den endnu</li>
                  </ul>
                  <p>
                    <strong>Løsning:</strong> Forbedre indholdet, tilføj flere interne links til siden, 
                    og vær tålmodig. Hvis indholdet er kvalitet, vil Google typisk indeksere det inden 
                    for et par uger.
                  </p>

                  <h3>Problem 2: "Crawled - Currently Not Indexed"</h3>
                  <p>
                    <strong>Hvad det betyder:</strong> Google har crawlet siden, men besluttet den 
                    ikke er værdifuld nok til at indeksere.
                  </p>
                  <p>
                    <strong>Mulige årsager:</strong>
                  </p>
                  <ul className="simple-list">
                    <li>Lav kvalitet indhold</li>
                    <li>Siden tilbyder ikke unik værdi</li>
                    <li>Tekniske problemer (men siden kunne crawles)</li>
                  </ul>
                  <p>
                    <strong>Løsning:</strong> Dette er Googles måde at sige "vi synes ikke denne 
                    side er god nok". Du skal enten forbedre indholdet markant eller acceptere at 
                    siden måske ikke fortjener at være i indekset.
                  </p>

                  <h3>Problem 3: "Excluded by 'noindex' tag"</h3>
                  <p>
                    <strong>Hvad det betyder:</strong> Der er en noindex meta tag på siden der 
                    fortæller Google ikke at indeksere den.
                  </p>
                  <p>
                    <strong>Mulige årsager:</strong>
                  </p>
                  <ul className="simple-list">
                    <li>Du har bevidst sat noindex (f.eks. på admin sider)</li>
                    <li>Din staging site indstilling er ved et uheld stadig aktiv</li>
                    <li>Et plugin eller tema har tilføjet noindex</li>
                  </ul>
                  <p>
                    <strong>Løsning:</strong> Inspicer sidens HTML og fjern noindex tagget hvis 
                    det ikke er intentionelt. På WordPress kan dette ofte ske hvis du har sat 
                    "Discourage search engines" i Settings.
                  </p>

                  <h3>Problem 4: "Redirect Error"</h3>
                  <p>
                    <strong>Hvad det betyder:</strong> Google forsøgte at crawle siden men blev 
                    mødt af en redirect der ikke fungerer korrekt.
                  </p>
                  <p>
                    <strong>Mulige årsager:</strong>
                  </p>
                  <ul className="simple-list">
                    <li>Redirect loops (side A redirecter til B som redirecter til A)</li>
                    <li>For lange redirect chains (A → B → C → D → E)</li>
                    <li>Redirects til ikke-eksisterende sider</li>
                  </ul>
                  <p>
                    <strong>Løsning:</strong> Brug et værktøj som Screaming Frog til at identificere 
                    redirect problemer og fix dem. Redirects bør være direkte (A → B) og ikke chained.
                  </p>

                  <h3>Problem 5: "Server Error (5xx)"</h3>
                  <p>
                    <strong>Hvad det betyder:</strong> Din server returnerede en fejl da Googlebot 
                    forsøgte at crawle siden.
                  </p>
                  <p>
                    <strong>Løsning:</strong> Check dine server logs for at se hvad der går galt. 
                    Dette kan være alt fra server overbelastning til konfigurationsproblemer. Kontakt 
                    din hosting provider hvis problemet fortsætter.
                  </p>

                  <div className="warning-box">
                    <h4>⚠️ Vigtig Note</h4>
                    <p>
                      Gentagne server errors kan føre til at Google reducerer dit crawl budget markant. 
                      Fix tekniske problemer hurtigst muligt for at undgå langsigtede konsekvenser.
                    </p>
                  </div>

                  <h3>Problem 6: "Duplicate Without User-Selected Canonical"</h3>
                  <p>
                    <strong>Hvad det betyder:</strong> Google har fundet flere versioner af samme 
                    side og du har ikke specificeret hvilken der er originalen.
                  </p>
                  <p>
                    <strong>Løsning:</strong> Tilføj canonical tags til alle versioner der peger 
                    på den foretrukne version. Eller brug 301 redirects til at konsolidere til én URL.
                  </p>
                </div>
              </section>

              {/* Konklusion */}
              <section className="artikel-section" id="konklusion">
                <h2>Konklusion - Indeksering som Fundament for SEO</h2>
                
                <div className="artikel-text">
                  <p>
                    Google's indekseringsproces er fascinerende kompleks, men fundamentalt logisk. 
                    Ved at forstå hvordan crawling, rendering og indeksering fungerer, kan du bygge 
                    hjemmesider der ikke bare ser godt ud for mennesker, men også er optimeret for 
                    søgemaskiner.
                  </p>

                  <p>
                    Husk de vigtigste takeaways:
                  </p>

                  <ul className="simple-list">
                    <li><strong>Crawlability er første skridt:</strong> Sørg for at Googlebot kan finde og crawle dine sider</li>
                    <li><strong>Kvalitet trumfer kvantitet:</strong> Fokuser på værdifuldt indhold frem for at bare producere mange sider</li>
                    <li><strong>Teknisk SEO er fundamentet:</strong> Site speed, mobilvenlig design og korrekt HTML struktur er kritiske</li>
                    <li><strong>Monitor og optimer løbende:</strong> Brug Google Search Console til at holde øje med indekseringssundhed</li>
                    <li><strong>Vær tålmodig:</strong> Indeksering tager tid - især for nye sites</li>
                  </ul>

                  <p>
                    At blive indekseret er kun begyndelsen. Den egentlige udfordring er at ranke 
                    højt for relevante søgninger. Men uden korrekt indeksering kommer du aldrig så 
                    langt. Det er det fundament alt andet SEO bygger på.
                  </p>

                  <div className="cta-box">
                    <h3>Har du brug for hjælp til at optimere din hjemmeside?</h3>
                    <p>
                      Hos Parla Branding specialiserer vi os i at bygge hjemmesider der er optimeret 
                      til Google fra dag ét. Vi håndterer alle de tekniske detaljer - fra sitemap 
                      generation til struktureret data - så din hjemmeside får den synlighed den fortjener.
                    </p>
                    <Link to="/kontakt" className="cta-button">
                      Kontakt os for en gratis konsultation
                    </Link>
                  </div>
                </div>
              </section>

              {/* Relateret Indhold */}
              <section className="related-content-section">
                <h3>Læs også disse artikler</h3>
                <div className="related-articles">
                  <div className="related-article">
                    <h4><a href="/artikler/seo-guide-2025">SEO Guide 2025</a></h4>
                    <p>Komplet guide til søgemaskineoptimering med alle de nyeste best practices og teknikker.</p>
                  </div>
                  
                  <div className="related-article">
                    <h4><a href="/artikler/mobilvenlige-hjemmesider">Mobilvenlige Hjemmesider</a></h4>
                    <p>Hvorfor mobile-first design er afgørende for moderne SEO og Google rankings.</p>
                  </div>
                  
                  <div className="related-article">
                    <h4><a href="/artikler/webdesign-trends-2025">Webdesign Trends 2025</a></h4>
                    <p>Opdag de nyeste trends i moderne webdesign og lær hvordan du skaber hjemmesider der konverterer.</p>
                  </div>
                </div>
              </section>

            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default GoogleIndeksering;